import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import api, { apiError, money } from "@/lib/api";
import { Amount } from "@/components/shared/Amount";
import { PageHeader } from "@/components/shared/DashKit";
import { useContent } from "@/context/ContentContext";
import { countryLabel } from "@/lib/labels";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CreditCard, Smartphone, Bitcoin, Loader2, Copy, CheckCircle2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useTranslation } from "react-i18next";

const METHODS = [
  { id: "afripay", label: "Carte Bancaire", desc: "Visa & Mastercard", icon: CreditCard },
  { id: "mobile_money", label: "Mobile Money", desc: "PawaPay · Afrique", icon: Smartphone },
  { id: "usdt", label: "USDT — TRC20", desc: "Réseau TRON uniquement", icon: Bitcoin },
];

const MIN_BY_METHOD = { afripay: 5, mobile_money: 1, usdt: 12 };

export default function AddFunds() {
  const { t } = useContent();
  const { t: tr } = useTranslation();
  const ctx = useOutletContext();
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("afripay");
  const minAmount = MIN_BY_METHOD[method] ?? 1;
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(false);
  const [now, setNow] = useState(Date.now());

  // Mobile Money (PawaPay)
  const [mmConfig, setMmConfig] = useState(null);
  const [country, setCountry] = useState("");
  const [operator, setOperator] = useState("");
  const [numberId, setNumberId] = useState("");
  const [myNumbers, setMyNumbers] = useState(null);
  const [quote, setQuote] = useState(null);
  const [currency, setCurrency] = useState("");
  const [detected, setDetected] = useState(null);
  const [autoDone, setAutoDone] = useState(false);

  useEffect(() => {
    if (method === "mobile_money" && !myNumbers) {
      api.get("/me/payment-numbers").then((r) => setMyNumbers(r.data.numbers.filter((n) => n.status === "verified"))).catch(() => setMyNumbers([]));
    }
  }, [method, myNumbers]);

  useEffect(() => {
    if (method === "mobile_money" && !mmConfig) {
      api.get("/pawapay/config").then((r) => setMmConfig(r.data)).catch(() => {});
    }
  }, [method, mmConfig]);

  useEffect(() => {
    if (method !== "mobile_money" || autoDone || !myNumbers || !mmConfig) return;
    setAutoDone(true);
    const pick = myNumbers.find((n) => n.is_primary) || myNumbers[0];
    if (pick) setNumberId(pick.id);
  }, [method, autoDone, myNumbers, mmConfig]);

  useEffect(() => {
    if (!numberId || !mmConfig) { setDetected(null); return; }
    let live = true;
    api.get(`/pawapay/detect?number_id=${numberId}`).then((r) => {
      if (!live) return;
      setDetected(r.data);
      const c = mmConfig.countries.find((x) => x.code === r.data.country);
      if (c) {
        setCountry(c.code);
        const o = r.data.operator && c.operators.find((x) => x.code === r.data.operator);
        setOperator(o ? o.code : "");
        setCurrency(o && o.currencies.length === 1 ? o.currencies[0].currency : "");
      }
    }).catch(() => live && setDetected(null));
    return () => { live = false; };
  }, [numberId, mmConfig]);

  useEffect(() => {
    const amt = Number(amount);
    if (method !== "mobile_money" || !operator || !currency || !(amt > 0)) { setQuote(null); return; }
    const t = setTimeout(() => {
      api.get(`/pawapay/quote?operator=${operator}&country=${country}&currency=${currency}&amount=${amt}`).then((r) => setQuote(r.data)).catch(() => setQuote(null));
    }, 400);
    return () => clearTimeout(t);
  }, [amount, operator, currency, country, method]);

  const selectedCountry = mmConfig?.countries?.find((c) => c.code === country);
  const operators = selectedCountry?.operators || [];
  const selectedOp = operators.find((o) => o.code === operator);
  const currencies = selectedOp?.currencies || [];
  const selectedNumber = (myNumbers || []).find((n) => n.id === numberId);
  const countryMismatch = !!(selectedNumber && selectedCountry && !String(selectedNumber.msisdn).startsWith(String(selectedCountry.dial || "").replace("+", "")));
  const operatorMismatch = !!(detected && detected.number_id === numberId && detected.operator && operator && detected.operator !== operator);
  const mmBlocked = method === "mobile_money" && (countryMismatch || operatorMismatch || !currency);

  const create = async () => {
    const amt = Number(amount);
    if (!(amt >= minAmount)) return toast.error(`${t("client.af.min_prefix", "Montant minimum")} : $${minAmount}`);
    setLoading(true);
    try {
      if (method === "mobile_money") {
        if (!country || !operator) { setLoading(false); return toast.error(t("client.af.select_country_op", "Sélectionnez un pays et un opérateur")); }
        if (!numberId) { setLoading(false); return toast.error(tr("af.num.select_required")); }
        if (!currency) { setLoading(false); return toast.error(tr("af.mm.choose_currency")); }
        if (countryMismatch || operatorMismatch) { setLoading(false); return toast.error(tr("af.mm.mismatch")); }
        const { data } = await api.post("/pawapay/deposit", { amount: amt, country, operator, number_id: numberId, currency });
        setPayment(data); setDone(false);
      } else if (method === "usdt") {
        const { data } = await api.post("/payments/nowpayments/create", { amount: amt });
        setPayment({ ...data, _np: true }); setDone(false);
      } else if (method === "afripay") {
        const { data } = await api.post("/payments/afripay/create", { amount: amt });
        window.location.href = data.checkout_url;
        return;
      } else {
        const { data } = await api.post("/payments", { amount: amt, method });
        setPayment(data); setDone(false);
      }
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const simulate = async () => {
    setConfirming(true);
    try {
      await api.post(`/payments/${payment.id}/simulate`);
      toast.success(t("client.af.paid_ok", "Paiement confirmé, portefeuille crédité !"));
      setDone(true); ctx?.reloadTop && ctx.reloadTop();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setConfirming(false); }
  };

  const isRealMM = method === "mobile_money" && mmConfig?.demo === false;
  const afpNet = method === "afripay" ? (parseFloat(amount) || 0) : 0;
  const afpFee = Math.round(afpNet * 0.05 * 100) / 100;
  const afpTotal = Math.round((afpNet + afpFee) * 100) / 100;
  const mmNet = method === "mobile_money" ? (parseFloat(amount) || 0) : 0;
  const mmFee = Math.round(mmNet * 0.05 * 100) / 100;
  const mmTotal = Math.round((mmNet + mmFee) * 100) / 100;
  const npRemaining = payment?._np ? Math.max(0, Date.parse(payment.expires_at) - now) : 0;
  const npMM = String(Math.floor(npRemaining / 60000)).padStart(2, "0");
  const npSS = String(Math.floor((npRemaining % 60000) / 1000)).padStart(2, "0");

  const checkMmStatus = async () => {
    try {
      const { data } = await api.get(`/pawapay/deposit/${payment.id}/status`);
      if (data.credited || data.status === "completed") { setDone(true); ctx?.reloadTop && ctx.reloadTop(); return true; }
      if (data.status === "failed") { toast.error(t("client.af.paid_failed", "Paiement échoué ou refusé par l'opérateur")); return true; }
    } catch { /* keep polling */ }
    return false;
  };

  useEffect(() => {
    if (!payment || !isRealMM || done) return;
    let stop = false;
    const tick = async () => { if (!stop && await checkMmStatus()) stop = true; };
    tick();
    const t = setInterval(tick, 4000);
    return () => { stop = true; clearInterval(t); };
  }, [payment, isRealMM, done]); // eslint-disable-line

  // NOWPayments USDT: poll server status (which reconciles with NOWPayments and credits).
  useEffect(() => {
    if (!payment || !payment._np || done) return;
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/payments/nowpayments/${payment.id}/status`);
        if (!stop && (data.credited || data.status === "completed")) { setDone(true); ctx?.reloadTop && ctx.reloadTop(); stop = true; }
      } catch { /* keep polling */ }
    };
    tick();
    const t = setInterval(tick, 15000);
    return () => { stop = true; clearInterval(t); };
  }, [payment, done]); // eslint-disable-line

  // Live countdown tick for the 30-min USDT payment window.
  useEffect(() => {
    if (!payment?._np) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [payment]);

  return (
    <div>
      <PageHeader title={t("wallet.addfunds.title", "Ajouter des fonds")} subtitle={t("wallet.addfunds.subtitle", "Rechargez votre portefeuille via Mobile Money, carte ou USDT.")} />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-6">
          <label className="text-sm font-medium">{t("wallet.addfunds.amount_label", "Montant (USD)")}</label>
          <div className="mt-1 flex items-center gap-2 rounded-lg border border-border bg-secondary/50 px-3">
            <span className="text-muted-foreground">$</span>
            <input type="number" data-testid="funds-amount" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="50.00" className="w-full bg-transparent py-2.5 text-sm outline-none" />
          </div>
          <div className="mt-2 text-xs" data-testid="min-amount-hint">
            <span className={Number(amount) > 0 && Number(amount) < minAmount ? "font-medium text-red-500" : "text-muted-foreground"}>
              {t("client.af.min_prefix", "Montant minimum")} : ${minAmount}
            </span>
          </div>
          <div className="mt-3 grid grid-cols-4 gap-2">
            {[10, 25, 50, 100].map((v) => <button key={v} onClick={() => setAmount(String(v))} data-testid={`quick-amount-${v}`} className="min-h-10 min-w-0 rounded-full border border-border px-2 py-2 text-sm hover:bg-secondary">${v}</button>)}
          </div>

          <div className="mt-6 text-sm font-medium">{t("wallet.addfunds.method_label", "Méthode de paiement")}</div>
          <div className="mt-2 space-y-2">
            {METHODS.map((m) => (
              <button key={m.id} data-testid={`method-${m.id}`} onClick={() => { setMethod(m.id); setPayment(null); }}
                className={`flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors ${method === m.id ? "border-accent bg-accent/5" : "border-border hover:bg-secondary"}`}>
                <m.icon className="h-5 w-5" />
                <div><div className="text-sm font-medium">{t(`payments.method.${m.id}.label`, m.label)}</div><div className="text-xs text-muted-foreground">{t(`payments.method.${m.id}.desc`, m.desc)}</div></div>
              </button>
            ))}
          </div>

          {method === "mobile_money" && (
            <div className="mt-5 space-y-3 rounded-lg border border-border bg-secondary/30 p-4" data-testid="mm-section">
              <div className="text-sm font-medium">{t("client.af.mm_africa", "Mobile Money — Afrique")}</div>
              {!mmConfig ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t("client.af.loading_ops", "Chargement des opérateurs…")}</div>
              ) : (
                <>
                  <div>
                    <label className="text-xs text-muted-foreground">{t("client.af.phone", "Numéro de téléphone")}</label>
                    {(myNumbers || []).length ? (
                      <select data-testid="mm-number-select" value={numberId} onChange={(e) => setNumberId(e.target.value)}
                        className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm outline-none">
                        <option value="">{tr("af.num.choose")}</option>
                        {myNumbers.map((n) => <option key={n.id} value={n.id}>+{n.msisdn}{n.is_primary ? tr("pn.primary_suffix") : ""}</option>)}
                      </select>
                    ) : (
                      <p className="mt-1 rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-700" data-testid="mm-no-verified-number">
                        {tr("af.num.none")}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">{t("client.af.country", "Pays")}</label>
                    <Select value={country} onValueChange={(v) => { setCountry(v); setOperator(""); setCurrency(""); setPayment(null); }}>
                      <SelectTrigger data-testid="mm-country" className="mt-1 w-full"><SelectValue placeholder={t("client.af.choose_country", "Choisir un pays")} /></SelectTrigger>
                      <SelectContent>
                        {mmConfig.countries.map((c) => (
                          <SelectItem key={c.code} value={c.code} data-testid={`mm-country-${c.code}`}>
                            <span className="mr-2">{c.flag}</span>{countryLabel(t, c.code, c.name)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {selectedCountry && (
                    <div>
                      <label className="text-xs text-muted-foreground">{t("client.af.operator", "Opérateur")}</label>
                      <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {operators.map((o) => (
                          <button key={o.code} type="button" data-testid={`mm-operator-${o.code}`}
                            onClick={() => { setOperator(o.code); setCurrency(o.currencies.length === 1 ? o.currencies[0].currency : ""); setPayment(null); }}
                            className={`rounded-lg border px-3 py-2 text-left text-sm transition-colors ${operator === o.code ? "border-accent bg-accent/5" : "border-border hover:bg-secondary"}`}>
                            {o.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedOp && currencies.length > 1 && (
                    <div data-testid="mm-currency-section">
                      <label className="text-xs text-muted-foreground">{tr("af.mm.currency")}</label>
                      <div className="mt-1 grid grid-cols-2 gap-2">
                        {currencies.map((c) => (
                          <button key={c.currency} type="button" data-testid={`mm-currency-${c.currency}`} onClick={() => { setCurrency(c.currency); setPayment(null); }}
                            className={`rounded-lg border px-3 py-2 text-sm transition-colors ${currency === c.currency ? "border-accent bg-accent/5" : "border-border hover:bg-secondary"}`}>{c.currency}</button>
                        ))}
                      </div>
                      {!currency && <p className="mt-1 text-xs text-amber-700">{tr("af.mm.choose_currency")}</p>}
                    </div>
                  )}
                  {(countryMismatch || operatorMismatch) && (
                    <p className="rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-600" data-testid="mm-mismatch-warning">
                      {countryMismatch ? tr("af.mm.country_mismatch") : tr("af.mm.operator_mismatch")}
                    </p>
                  )}
                  {selectedCountry && quote && quote.amount_local != null && quote.currency && (
                    <p className="text-xs text-accent" data-testid="mm-quote">≈ {quote.amount_local} {quote.currency} {t("client.af.quote_debited", "seront débités (frais TWP inclus · taux du jour)")}</p>
                  )}
                </>
              )}
            </div>
          )}

          {method === "afripay" && afpNet > 0 && (
            <div className="mt-5 rounded-lg border border-border bg-secondary/30 p-4 text-sm" data-testid="afripay-fee">
              <div className="flex justify-between"><span className="text-muted-foreground">{t("client.af.amount_credited", "Montant reçu sur le solde")}</span><span className="font-medium">${afpNet.toFixed(2)}</span></div>
              <div className="mt-1 flex justify-between"><span className="text-muted-foreground">{t("client.af.twp_fee", "Frais de service TWP (5%)")}</span><span className="font-medium">${afpFee.toFixed(2)}</span></div>
              <div className="mt-2 flex justify-between border-t border-border pt-2 font-semibold"><span>{t("client.af.total_charge", "Total à débiter")}</span><span data-testid="afripay-total">${afpTotal.toFixed(2)}</span></div>
            </div>
          )}

          {method === "mobile_money" && mmNet > 0 && operator && (
            <div className="mt-5 rounded-lg border border-border bg-secondary/30 p-4 text-sm" data-testid="mm-fee">
              <div className="mb-2 space-y-1 border-b border-border pb-2 text-xs" data-testid="mm-recap">
                <div className="flex justify-between"><span className="text-muted-foreground">{t("client.af.phone_short", "Téléphone")}</span><span className="font-mono">{selectedNumber ? `+${selectedNumber.msisdn}` : "—"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t("client.af.country", "Pays")}</span><span>{selectedCountry?.flag} {selectedCountry ? countryLabel(t, selectedCountry.code, selectedCountry.name) : "—"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t("client.af.operator", "Opérateur")}</span><span>{selectedOp?.name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{tr("af.mm.currency")}</span><span className="font-semibold" data-testid="mm-recap-currency">{currency || "—"}</span></div>
              </div>
              <div className="flex justify-between"><span className="text-muted-foreground">{t("client.af.amount_credited", "Montant reçu sur le solde")}</span><span className="font-medium">${mmNet.toFixed(2)}</span></div>
              <div className="mt-1 flex justify-between"><span className="text-muted-foreground">{t("client.af.twp_fee", "Frais de service TWP (5%)")}</span><span className="font-medium">${mmFee.toFixed(2)}</span></div>
              <div className="mt-2 flex justify-between border-t border-border pt-2 font-semibold"><span>{t("client.af.total_charge", "Total à débiter")}</span><span data-testid="mm-total">${mmTotal.toFixed(2)}</span></div>
              {quote && quote.amount_local != null && quote.currency && (
                <div className="mt-1 flex justify-between text-xs text-accent"><span>{t("client.af.mm_equiv", "Équivalent Mobile Money")}</span><span data-testid="mm-total-local">≈ {quote.amount_local} {quote.currency}</span></div>
              )}
              <p className="mt-2 text-[11px] text-muted-foreground">{t("client.af.operator_fees_note", "Des frais opérateur Mobile Money peuvent s'appliquer séparément et ne sont pas des frais TWP.")}</p>
            </div>
          )}

          <button onClick={create} disabled={loading || !amount || Number(amount) < minAmount || mmBlocked} data-testid="create-payment-btn"
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-primary py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-accent disabled:opacity-50">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {method === "mobile_money" ? t("wallet.addfunds.pay_mm", "Payer via Mobile Money") : method === "afripay" ? t("client.af.pay_card", "Payer par carte") : t("wallet.addfunds.continue", "Continuer")}
          </button>
        </div>

        <div className="rounded-xl border border-border bg-card p-6">
          {!payment ? (
            <div className="flex h-full items-center justify-center text-center text-sm text-muted-foreground">{t("client.af.select_prompt", "Sélectionnez un montant et une méthode, puis cliquez sur Continuer.")}</div>
          ) : done ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <CheckCircle2 className="h-14 w-14 text-emerald-500" />
              <div className="mt-4 font-display text-lg font-bold">{t("client.af.confirmed", "Paiement confirmé")}</div>
              <p className="mt-1 text-sm text-muted-foreground"><Amount value={payment.amount} /> {t("client.af.credited_suffix", "ont été crédités sur votre portefeuille.")}</p>
            </div>
          ) : (
            <div>
              <div className="font-display font-semibold">{t("client.af.confirmation", "Confirmation du paiement")}</div>
              <div className="mt-4 space-y-2 rounded-lg border border-border p-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">{t("client.af.amount", "Montant")}</span><span className="font-display font-bold"><Amount value={payment.amount} /></span></div>
                {payment.currency_local && <div className="flex justify-between"><span className="text-muted-foreground">{t("client.af.local_amount", "Montant local")}</span><span className="font-display font-bold">{payment.amount_local} {payment.currency_local}</span></div>}
                <div className="flex justify-between"><span className="text-muted-foreground">{t("client.af.reference", "Référence")}</span><span className="font-mono text-xs">{payment.reference}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t("client.af.status", "Statut")}</span><span className="capitalize">{payment.status}</span></div>
                {method === "mobile_money" && payment.meta && (
                  <>
                    <div className="flex justify-between"><span className="text-muted-foreground">{t("client.af.operator", "Opérateur")}</span><span>{payment.meta.flag} {payment.meta.operator_name}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">{t("client.af.phone_short", "Téléphone")}</span><span className="font-mono text-xs">{payment.meta.phone}</span></div>
                  </>
                )}
              </div>

              {method === "card" && <p className="mt-4 rounded-lg bg-secondary/50 p-3 text-xs text-muted-foreground">{t("client.af.card_note", "Redirection vers la passerelle de carte sécurisée. Aucune donnée de carte n'est stockée.")}</p>}
              {method === "mobile_money" && <p className="mt-4 rounded-lg bg-secondary/50 p-3 text-xs text-muted-foreground">{payment.instructions || t("client.af.mm_note", "Une demande de paiement Mobile Money a été envoyée via PawaPay. Confirmez sur votre téléphone.")}</p>}
              {method === "usdt" && payment._np && (
                <div className="mt-4 space-y-3" data-testid="usdt-nowpayments">
                  <div className="flex justify-center">
                    <div className="rounded-lg bg-white p-3">
                      <QRCodeSVG value={`tron:${payment.pay_address}?amount=${payment.pay_amount}`} size={190} />
                    </div>
                  </div>
                  <div className="rounded-lg bg-secondary/50 p-3 text-xs">
                    <div className="mb-1 font-medium text-foreground">{t("client.af.exact_amount", "Montant exact à envoyer")}</div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 rounded bg-background p-2 font-mono text-sm font-bold" data-testid="usdt-pay-amount">{payment.pay_amount} USDT</code>
                      <button onClick={() => { navigator.clipboard.writeText(payment.pay_amount); toast.success(t("client.af.copied", "Copié")); }} className="rounded border border-border p-2"><Copy className="h-3.5 w-3.5" /></button>
                    </div>
                    <div className="mb-1 mt-3 font-medium text-foreground">{t("client.af.deposit_addr", "Adresse de dépôt — réseau TRC20 (TRON)")}</div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 break-all rounded bg-background p-2 font-mono text-[11px]" data-testid="usdt-pay-address">{payment.pay_address}</code>
                      <button onClick={() => { navigator.clipboard.writeText(payment.pay_address); toast.success(t("client.af.copied", "Copié")); }} className="rounded border border-border p-2"><Copy className="h-3.5 w-3.5" /></button>
                    </div>
                    <p className="mt-2 text-muted-foreground">{t("client.af.usdt_warning", "⚠️ Envoyez uniquement de l'USDT sur le réseau TRC20. Tout autre réseau entraînera une perte de fonds.")}</p>
                  </div>
                  <div className="text-center text-sm" data-testid="usdt-countdown">
                    {npRemaining > 0
                      ? <>{t("client.af.addr_valid", "Adresse valable encore")} <span className="font-mono font-bold">{npMM}:{npSS}</span></>
                      : <span className="text-red-500">{t("client.af.expired", "Paiement expiré — créez un nouveau paiement.")}</span>}
                  </div>
                  <div className="flex items-center justify-center gap-2 text-sm text-accent">
                    <Loader2 className="h-4 w-4 animate-spin" /> {t("client.af.waiting_payment", "En attente du paiement… (crédit automatique)")}
                  </div>
                </div>
              )}

              {isRealMM ? (
                <div className="mt-5 rounded-lg border border-accent/40 bg-accent/5 p-4 text-center" data-testid="mm-waiting">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin text-accent" />
                  <p className="mt-2 text-sm font-medium">{t("client.af.waiting_phone", "En attente de votre confirmation sur le téléphone…")}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{t("client.af.approve_pin", "Approuvez la demande Mobile Money (code PIN). Le crédit est automatique.")}</p>
                  <button onClick={checkMmStatus} data-testid="mm-check-status" className="mt-3 rounded-full border border-border px-4 py-1.5 text-xs font-medium hover:bg-secondary">{t("client.af.check_now", "Vérifier maintenant")}</button>
                </div>
              ) : (payment._np || !payment.simulate_available) ? null : (
                <>
                  <button onClick={simulate} disabled={confirming} data-testid="simulate-payment-btn"
                    className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-accent py-2.5 text-sm font-medium text-white transition-colors hover:opacity-90 disabled:opacity-50">
                    {confirming && <Loader2 className="h-4 w-4 animate-spin" />} {t("client.af.confirm_payment", "Confirmer le paiement")}
                  </button>
                  <p className="mt-2 text-center text-[11px] text-muted-foreground">{t("client.af.server_note", "Le portefeuille est crédité après confirmation côté serveur.")}</p>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
